<template>
  <div id="AdminBilling" class="flex-grow-1 text-start overflow-auto w-100">
    <div class="container-fluid py-4">
      <h1 class="mb-4">Billing</h1>

      <TabsContainer
        :tabs="tabs"
        :active-tab="activeTab ?? 'invoices'"
        @change:tab="setTab"
      >
        <template #tab-invoices>
          <InvoicesOverviewPanel @view:company="openCompany" />
        </template>

        <template #tab-upcoming>
          <UpcomingInvoicesPanel @view:company="openCompany" />
        </template>

        <template #tab-payouts>
          <PendingPayoutsPanel @view:company="openCompany" />
        </template>

        <template #tab-company>
          <CompanyBillingPanel v-model:company-id="selectedCompanyId" />
        </template>

        <template #tab-ledger>
          <LedgerPanel />
        </template>

        <template #tab-corrections>
          <CorrectionsPanel />
        </template>

        <template #tab-reports>
          <MonthlyServiceFeeReport />
        </template>
      </TabsContainer>
    </div>
  </div>
</template>

<script setup lang="ts">
import TabsContainer from "@/components/common/tabs/TabsContainer.vue";
import type { Tab } from "@/composables/useTabs";
import { useTabs } from "@/composables/useTabs";
import { useQueryManager } from "@/composables/useQueryManager";
import LedgerPanel from "@/money/admin/ledger/LedgerPanel.vue";
import MonthlyServiceFeeReport from "../MonthlyServiceFeeReport.vue";
import CompanyBillingPanel from "./CompanyBillingPanel.vue";
import CorrectionsPanel from "./CorrectionsPanel.vue";
import InvoicesOverviewPanel from "./InvoicesOverviewPanel.vue";
import PendingPayoutsPanel from "./PendingPayoutsPanel.vue";
import UpcomingInvoicesPanel from "./UpcomingInvoicesPanel.vue";

defineOptions({
  name: "AdminBillingView",
});

const tabs: Tab[] = [
  { id: "invoices", name: "Invoices", icon: "file-invoice-dollar" },
  { id: "upcoming", name: "Upcoming invoices", icon: "calendar-alt" },
  { id: "payouts", name: "Pending payouts", icon: "dollar-sign" },
  { id: "company", name: "Company detail", icon: "building" },
  { id: "ledger", name: "Ledger", icon: "list" },
  { id: "corrections", name: "Corrections", icon: "edit" },
  { id: "reports", name: "Reports", icon: "file-alt" },
];

const { activeTab, setTab } = useTabs("admin-billing", {
  tabs,
  defaultTabId: "invoices",
});

const { getQueryValue, parsers, updateQuery } = useQueryManager();
const selectedCompanyId = getQueryValue("company_id", parsers.number);

// Cross-link: jump from an all-companies view straight into one company's
// billing detail (fee items, bundles, settlement). Single query update so the
// two writes can't clobber each other.
const openCompany = (companyId: number) => {
  updateQuery({
    company_id: companyId.toString(),
    "admin-billing-active-tab": "company",
  });
};
</script>
